import { formatMm, MAX_SHELVES, type AssemblyInstance, type ShelfSet } from '@scs/domain-engine';
import { IntInput, MmInput } from '../lib/ui';
import type { EditorStore } from './store';

type Exec = EditorStore['exec'];

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="prop-row">
      <label>{label}</label>
      {children}
    </div>
  );
}

/**
 * Shelf set ("Police") of an assembly: count, clearance, thickness; deleting a single shelf and
 * bringing deleted ones back. `shelfIndex` is the shelf selected in the scene (if any).
 */
export function ShelfProperties({
  inst,
  set,
  shelfIndex,
  exec,
  disabled,
  onSelectSet,
  onRemoved,
}: {
  inst: AssemblyInstance;
  set: ShelfSet;
  shelfIndex: number | null;
  exec: Exec;
  disabled: boolean;
  onSelectSet(): void;
  onRemoved(): void;
}) {
  const lock = disabled || inst.locked;
  const edit = (label: string, fn: (s: ShelfSet) => void) =>
    exec(label, (d) => {
      const s = d.assemblies.find((a) => a.id === inst.id)?.shelfSets?.find((x) => x.id === set.id);
      if (s) fn(s);
    });
  const name = (k: string | null) => (k ? (inst.panels.find((p) => p.key === k)?.label ?? k) : '—');
  const deleted = set.deleted.filter((i) => i <= set.count);

  return (
    <div className="prop-group shelf-group">
      <h3>
        ▤ {set.label}
        {shelfIndex !== null && <span className="faint"> · polica {shelfIndex}</span>}
      </h3>
      <Row label="Naziv">
        <input
          className="input"
          style={{ height: 26 }}
          defaultValue={set.label}
          key={set.label}
          disabled={lock}
          onKeyDown={(e) => e.stopPropagation()}
          onBlur={(e) => e.target.value.trim() && e.target.value !== set.label && edit('Naziv polica', (s) => (s.label = e.target.value.trim().slice(0, 200)))}
        />
      </Row>
      <Row label="Broj polica">
        <IntInput
          label="Broj polica"
          value={set.count}
          min={1}
          max={MAX_SHELVES}
          disabled={lock}
          onCommit={(v) =>
            edit('Broj polica', (s) => {
              s.count = v;
              s.deleted = s.deleted.filter((i) => i <= v);
            })
          }
        />
      </Row>
      <Row label="Zazor">
        <MmInput label="Zazor" value={set.gap} min={0} max={100} disabled={lock} onCommit={(v) => edit('Zazor polica', (s) => (s.gap = v))} />
      </Row>
      <Row label="Debljina">
        <MmInput label="Debljina polica" value={set.thickness} min={1} max={100} disabled={lock} onCommit={(v) => edit('Debljina polica', (s) => (s.thickness = v))} />
      </Row>
      <p className="faint" style={{ fontSize: 11, margin: '4px 0' }}>
        Između: {name(set.bottomKey)} – {name(set.topKey)}
        {set.leftKey || set.rightKey ? ` · prostor: ${name(set.leftKey)} – ${name(set.rightKey)} (zazor ${formatMm(set.gap)} mm sa svake strane)` : ' · širina granične ploče'}
      </p>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
        {shelfIndex !== null && (
          <button
            className="btn sm ghost danger"
            disabled={lock}
            onClick={() => {
              edit('Obriši policu', (s) => (s.deleted = [...new Set([...s.deleted, shelfIndex])].sort((a, b) => a - b)));
              onSelectSet();
            }}
          >
            Obriši ovu policu
          </button>
        )}
        {deleted.length > 0 && (
          <button className="btn sm" disabled={lock} onClick={() => edit('Vrati obrisane police', (s) => (s.deleted = []))} title={`Obrisane: ${deleted.join(', ')}`}>
            ↺ Vrati obrisane ({deleted.length})
          </button>
        )}
        <button
          className="btn sm ghost danger"
          disabled={lock}
          onClick={() => {
            exec('Obriši police', (d) => {
              const a = d.assemblies.find((x) => x.id === inst.id);
              if (a) a.shelfSets = (a.shelfSets ?? []).filter((s) => s.id !== set.id);
            });
            onRemoved();
          }}
        >
          Obriši sve police
        </button>
      </div>
    </div>
  );
}
